import * as React from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  Typography,
  Paper,
  Grid,
} from "@mui/material";
import {
  Close as CloseIcon,
  FormatBold as FormatBoldIcon,
  FormatItalic as FormatItalicIcon,
  FormatUnderlined as FormatUnderlinedIcon,
  FormatListBulleted as FormatListBulletedIcon,
  FormatListNumbered as FormatListNumberedIcon,
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  Delete as DeleteIcon,
  EditDocument as EditDocumentIcon,
  Payments as PaymentsIcon,
  ChangeCircle as ChangeCircleIcon,
  AddCircle as AddCircleIcon,
  NoteAdd,
} from "@mui/icons-material";
import AddNoteDialog from ".";
/**
 * TimeLineNotes
 * A two-pane Box (no Dialog) combining an Add Note form with a right-side Activity Timeline.
 *
 * Props:
 * - loanId?: string
 * - events?: Array<{
 *     id: string | number;
 *     type: "note" | "payment" | "status" | "created" | string;
 *     title: string;
 *     actor: string;
 *     when: string; // humanized string or ISO
 *     body?: string;
 *   }>
 * - onClose?: () => void
 * - onSave?: (payload: { html: string; text: string; files: File[] }) => void
 */
export default function TimeLineNotes({ loanId,loanInfo, onClose, onSave }) {
  const editorRef = React.useRef(null);
  const fileInputRef = React.useRef(null);
  const [files, setFiles] = React.useState([]);
  const [events, setEvents] = React.useState([]);
  const [openAddNoteDialog, setOpenAddNoteDialog] = React.useState(false);

  const data = events && events.length ? events : [];

  const exec = (cmd) => {
    if (!editorRef.current) return;
    document.execCommand(cmd, false, null);
    editorRef.current.focus();
  };

  const onDropFiles = (e) => {
    const picked = e.target.files ? Array.from(e.target.files) : [];
    if (picked.length) setFiles((prev) => [...prev, ...picked]);
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleSave = () => {
    const div = editorRef.current;
    const html = div ? div.innerHTML : "";
    const text = div ? div.innerText : "";
    onSave?.({ html, text, files });
  };

  const iconFor = (type) => {
    switch (type) {
      case "note":
        return <EditDocumentIcon sx={{ color: "primary.main" }} />;
      case "payment":
        return <PaymentsIcon sx={{ color: "success.main" }} />;
      case "status":
        return <ChangeCircleIcon sx={{ color: "warning.main" }} />;
      case "created":
      default:
        return <EditDocumentIcon sx={{ color: "secondary.main" }} />;
    }
  };

  React.useEffect(() => {
    fetchEvents();
  }, []);
  const fetchEvents = async () => {
    const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/prestamos/${loanId}/notas`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
      }
    });
    const data = await response.json();
    setEvents(data);
  };

  return (
    <Paper variant="outlined" sx={{ width: '100%', maxWidth: 1200, mx: 'auto', p: 0, overflow: 'hidden', borderRadius: 2, mt: 2 }}>
      <Grid container>

        {/* Right pane: Timeline */}
        <Grid item size={12} >
          <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Button variant="outlined" color="primary" startIcon={<NoteAdd/>} sx={{mb: 2,fontWeight:"bold",borderColor:"primary.main",color:"primary.main"}}  onClick={() => {setOpenAddNoteDialog(true)}}>
              Agregar Nota
            </Button>
            <Box sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
              <Stack spacing={3}>
                {data.map((ev, idx) => (
                  <Box key={ev.id} sx={{ position: 'relative', pl: 6 }}>
                    {/* vertical connector */}
                    {idx !== data.length - 1 && (
                      <Box sx={{ position: 'absolute', left: 22, top: 28, bottom: -6, width: 2, bgcolor: 'divider' }} />
                    )}
                    {/* dot */}
                    <Avatar sx={{ position: 'absolute', left: 0, top: 0, width: 36, height: 36, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
                      {iconFor("note")}
                    </Avatar>

                    <Typography variant="subtitle2" fontWeight={600}>{ev.nota}</Typography>
                    <Typography variant="caption" color="text.secondary">Por: {ev.created_by_nombre} — {ev.created_at}</Typography>
                    {ev.body && (
                      <Paper variant="outlined" sx={{ mt: 1, p: 1.5, bgcolor: 'grey.50' }}>
                        <Typography variant="body2">{ev.nota}</Typography>
                      </Paper>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <AddNoteDialog
        open={openAddNoteDialog}
        loanId={loanId}
        loanInfo={loanInfo}
        onClose={() => {setOpenAddNoteDialog(false);fetchEvents()}}
      />

    </Paper>
  );
}

